// src/pages/LiveBettingPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { FootballAPI } from '../services/footballApi';
import { useBetSlip } from '../context/BetSlipContext';
import MatchCard from '../components/sports/MatchCard';
import { 
  FiRefreshCw, FiActivity, FiTrendingUp, FiClock, 
  FiFilter, FiGrid, FiList, FiChevronRight,
  FiUsers, FiTarget, FiBarChart2
} from 'react-icons/fi';

const LiveBettingPage = () => {
  const navigate = useNavigate();
  const { addToBetSlip, betslipCount } = useBetSlip();
  
  const [liveEvents, setLiveEvents] = useState([]);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    activeBets: 0,
    liveVolume: 0
  });
  
  const refreshInterval = useRef(null);

  // League priority for sorting
  const getLeaguePriority = (leagueId, leagueName) => {
    const priorityMap = {
      1: 1, 2: 2, 39: 3, 140: 4, 135: 5, 78: 6, 61: 7
    };
    if (priorityMap[leagueId]) return priorityMap[leagueId];
    const name = leagueName?.toLowerCase() || '';
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    return 999;
  };

  // Fetch live matches from API
  const fetchLiveMatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await FootballAPI.getLiveFixtures();
      
      if (response.success && response.data) {
        const liveData = response.data;
        
        if (liveData.length > 0) {
          // Group by league
          const grouped = {};
          liveData.forEach(fixture => {
            const leagueId = fixture.league_id;
            if (!grouped[leagueId]) {
              grouped[leagueId] = {
                league_id: leagueId,
                league_name: fixture.league_name,
                league_logo: fixture.league_logo,
                country: fixture.country,
                fixtures: []
              };
            }
            
            // Format the fixture for MatchCard with all odds
            const formattedFixture = {
              ...fixture,
              odds: {
                home: fixture.odds?.home || null,
                draw: fixture.odds?.draw || null,
                away: fixture.odds?.away || null
              },
              isLive: ['1H', 'HT', '2H', 'ET', 'P'].includes(fixture.status),
              status: fixture.status,
              elapsed: fixture.elapsed || 0,
              home_score: fixture.home_score,
              away_score: fixture.away_score
            };
            
            grouped[leagueId].fixtures.push(formattedFixture);
          });
          
          // Sort leagues by priority
          const sortedGroups = Object.values(grouped).sort((a, b) => {
            const priorityA = getLeaguePriority(a.league_id, a.league_name);
            const priorityB = getLeaguePriority(b.league_id, b.league_name);
            return priorityA - priorityB;
          });
          
          setGroupedMatches(sortedGroups);
          setLiveEvents(liveData);
          setStats(prev => ({
            ...prev,
            total: liveData.length,
            liveVolume: liveData.length * 45
          }));
          setLastUpdate(new Date());
        } else {
          setLiveEvents([]);
          setGroupedMatches([]);
          setError('No live matches at the moment');
        }
      } else {
        setError('Failed to load live matches');
      }
    } catch (err) {
      setError('Failed to load live matches');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLiveMatches();
  };

  // Handle match card click
  const handleMatchCardClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  // Initial load and auto-refresh
  useEffect(() => {
    fetchLiveMatches();
    
    // Auto-refresh every 15 seconds for live matches
    refreshInterval.current = setInterval(() => {
      if (!loading && !refreshing) {
        fetchLiveMatches();
      }
    }, 15000);
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [fetchLiveMatches]);

  // Filter matches by category
  const getFilteredMatches = useCallback(() => {
    if (selectedCategory === 'all') {
      return groupedMatches;
    }
    
    return groupedMatches.filter(group => {
      const leagueName = group.league_name?.toLowerCase() || '';
      const categoryMap = {
        'football': ['premier league', 'la liga', 'bundesliga', 'serie a', 'ligue 1', 'champions league', 'world cup'],
        'basketball': ['nba', 'basketball'],
        'tennis': ['tennis', 'atp', 'wta'],
        'esports': ['esports', 'csgo', 'dota', 'league of legends'],
      };
      
      const keywords = categoryMap[selectedCategory] || [];
      return keywords.some(keyword => leagueName.includes(keyword));
    });
  }, [selectedCategory, groupedMatches]);

  const filteredGroups = getFilteredMatches();
  const allMatches = filteredGroups.flatMap(group => group.fixtures);

  // Categories with icons
  const categories = [
    { id: 'all', label: 'All Sports', icon: <FiActivity className="w-4 h-4" /> },
    { id: 'football', label: 'Football', icon: '⚽' },
    { id: 'basketball', label: 'Basketball', icon: '🏀' },
    { id: 'tennis', label: 'Tennis', icon: '🎾' },
    { id: 'esports', label: 'Esports', icon: '🎮' },
    { id: 'baseball', label: 'Baseball', icon: '⚾' },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500"></div>
              <p className="text-gray-400 mt-4">Loading live events...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Live Betting</h1>
              {allMatches.length > 0 && (
                <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded-full">
                  {allMatches.length} live
                </span>
              )}
            </div>
            <p className="text-gray-400 text-sm">Bet on ongoing matches with real-time American odds updates</p>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Link
              to="/betslip"
              className="relative px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Betslip
              {betslipCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {betslipCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* American Odds Badge */}
        <div className="mb-4 bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-center">
          <span className="text-gray-400 text-sm">
            Odds displayed in <span className="text-yellow-400 font-bold">American</span> format (+100, -150, etc.)
          </span>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg whitespace-nowrap flex-shrink-0 transition-colors ${
                selectedCategory === category.id
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span className="text-base">{category.icon}</span>
              {category.label}
            </button>
          ))}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-2 text-red-300 hover:text-red-200 text-sm underline"
            >
              Try again
            </button>
          </div>
        )}

       
        {/* Live Events Grid */}
        {allMatches.length === 0 ? (
          <div className="text-center py-12 bg-gray-900/50 rounded-xl">
            <div className="text-6xl mb-4">📺</div>
            <h3 className="text-white font-bold text-lg mb-2">No Live Matches</h3>
            <p className="text-gray-400 text-sm">Check back later for live action</p>
            <button
              onClick={() => navigate('/sports')}
              className="mt-4 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mx-auto"
            >
              Browse upcoming matches
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {filteredGroups.map((group) => (
              <div key={group.league_id} className="space-y-2 sm:space-y-3">
                {/* League Header */}
                <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg p-2 sm:p-3">
                  {group.league_logo && (
                    <img src={group.league_logo} alt={group.league_name} className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />
                  )}
                  <h3 className="font-bold text-white text-xs sm:text-sm">{group.league_name}</h3>
                  <span className="text-[10px] sm:text-xs text-gray-400 bg-gray-700 px-1.5 sm:px-2 py-0.5 rounded-full">
                    {group.fixtures.length}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-red-400">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                    LIVE
                  </span>
                </div>
                
                {/* Matches - Pass American odds always */}
                <div className={viewMode === 'grid' 
                  ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3" 
                  : "space-y-2 sm:space-y-3"
                }>
                  {group.fixtures.map((match) => (
                    <div 
                      key={match.id} 
                      onClick={() => handleMatchCardClick(match.id)}
                      className="cursor-pointer hover:scale-[1.02] transition-transform"
                    >
                      <MatchCard
                        match={match}
                        sport="football"
                        oddsFormat="american"
                        viewMode={viewMode}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live Betting Info */}
        <div className="mt-8 bg-gray-900 rounded-xl p-6 border border-gray-800">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FiTrendingUp className="text-red-500" />
            Live Betting Tips
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <span className="text-red-500 text-lg">⚡</span>
              <div>
                <h4 className="text-white font-semibold">Real-time Odds</h4>
                <p className="text-gray-400 text-xs">Odds update every 15 seconds in American format</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-yellow-500 text-lg">🎯</span>
              <div>
                <h4 className="text-white font-semibold">Higher Risk, Higher Reward</h4>
                <p className="text-gray-400 text-xs">Live odds offer better value</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-green-500 text-lg">📊</span>
              <div>
                <h4 className="text-white font-semibold">Live Stats Available</h4>
                <p className="text-gray-400 text-xs">Make informed decisions</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-500 text-lg">💰</span>
              <div>
                <h4 className="text-white font-semibold">Cash Out Available</h4>
                <p className="text-gray-400 text-xs">Secure profits before match ends</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LiveBettingPage;